import React, { useState, useEffect, useMemo, useCallback } from "react";
import { adminApiFetch, normalizeMenus, normalizeVillas } from "./api";

function Orders() {
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL;
    const [orders, setOrders] = useState([]);
    const [editedOrders, setEditedOrders] = useState([]);
    const [newOrders, setNewOrders] = useState([]);
    const [villas, setVillas] = useState([]);
    const [menus, setMenus] = useState([]);
    const [categories, setCategories] = useState([]);
    const [guests, setGuests] = useState([]);
    const [errorMsg, setErrorMsg] = useState("");
    const [deletedOrderIds, setDeletedOrderIds] = useState([]);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedGroupKey, setSelectedGroupKey] = useState("");

    const toGroupKey = useCallback((order) => {
        return [
            order.appointment_date || "",
            order.appointment_time || "",
            String(order.guest_id || ""),
            String(order.villa_id || ""),
        ].join("|");
    }, []);

    const flattenOrder = useCallback((o, allGuests) => {
        let detailsData = o.details;
        if (typeof detailsData === 'string') {
            try {
                const validJsonString = detailsData.replace(/'/g, '"');
                detailsData = JSON.parse(validJsonString);
            } catch (e) {
                console.error("Error parsing order details:", e);
                detailsData = [];
            }
        }

        const detail = detailsData && detailsData[0];
        const guest = allGuests.find(g => g.id === o.guest_id);

        let formattedTime = "";
        if (o.appointment_time) {
            const parts = o.appointment_time.split(':');
            if (parts.length >= 2) {
                const hour = parts[0].padStart(2, '0');
                const minute = parts[1].padStart(2, '0');
                formattedTime = `${hour}:${minute}`;
            }
        }

        const flattenedOrder = {
            ...o,
            appointment_time: formattedTime,
            name: guest ? guest.first_name : o.name || "",
            last_name: guest ? guest.last_name : "",
            guest_id: o.guest_id,
            arrival_date: guest ? guest.arrival_date : o.arrival_date,
        };

        if (detail) {
            const isNoneCategory = detail.category === 'none';
            flattenedOrder.detail_menu_id = detail.id ?? "";
            flattenedOrder.detail_name = isNoneCategory ? "None" : (detail.name || "");
            flattenedOrder.detail_quantity = detail.quantity ?? "";
            flattenedOrder.detail_category = detail.category || "";
            flattenedOrder.detail_price = detail.price ?? "";
            flattenedOrder.detail_discount = detail.discount ?? "";
        }

        return flattenedOrder;
    }, []);

    const normalizeTimeForApi = (value) => {
        if (!value) return "";
        const parts = String(value).split(":");
        if (parts.length < 2) return String(value);
        return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    };

    const unflattenOrder = (o, allMenus = []) => {
        const {
            details,
            detail_name, detail_quantity, detail_category, detail_price, detail_discount, detail_menu_id,
            ...rest
        } = o;

        const newOrder = { ...rest };

        newOrder.appointment_time = normalizeTimeForApi(newOrder.appointment_time);

        const resolvedMenu = allMenus.find(m => m.name === detail_name);
        const resolvedDetailId = detail_menu_id || resolvedMenu?.id || null;

        newOrder.details = [{
            id: resolvedDetailId,
            name: detail_name,
            quantity: detail_quantity === "" ? 0 : parseFloat(detail_quantity),
            category: detail_category,
            price: detail_price === "" ? 0 : parseFloat(detail_price),
            discount: detail_discount === "" ? 0 : parseFloat(detail_discount),
        }];
        return newOrder;
    };

    const fetchAllData = useCallback(async () => {
        setErrorMsg("");
        try {
            const [ordersRes, villasRes, menusRes, categoriesRes, guestsRes] = await Promise.all([
                adminApiFetch(`${baseUrl}/orders`),
                adminApiFetch(`${baseUrl}/villas`),
                adminApiFetch(`${baseUrl}/menus`),
                adminApiFetch(`${baseUrl}/menus/categories`),
                adminApiFetch(`${baseUrl}/guests`)
            ]);

            if (!ordersRes.ok) throw new Error("Error loading orders");
            if (!villasRes.ok) throw new Error("Error loading villas");
            if (!menusRes.ok) throw new Error("Error loading menus");
            if (!categoriesRes.ok) throw new Error("Error loading categories");
            if (!guestsRes.ok) throw new Error("Error loading guests");

            const ordersData = await ordersRes.json();
            const villasData = await villasRes.json();
            const menusData = await menusRes.json();
            const categoriesData = await categoriesRes.json();
            const guestsData = await guestsRes.json();

            setGuests(guestsData);
            setVillas(normalizeVillas(villasData));
            setMenus(normalizeMenus(menusData));
            setCategories(categoriesData);
            setOrders(ordersData);
            setEditedOrders(ordersData.map(o => flattenOrder(o, guestsData)));
            setNewOrders([]);
            setDeletedOrderIds([]);
            const first = ordersData[0] ? flattenOrder(ordersData[0], guestsData) : null;
            setSelectedGroupKey(first ? toGroupKey(first) : "");

        } catch (err) {
            setErrorMsg(err.message);
        }
    }, [baseUrl, flattenOrder, toGroupKey]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const changedOrders = useMemo(() =>
        editedOrders.filter(edited => {
            const original = orders.find(o => o.id === edited.id);
            if (!original) return false;
            const comparableOriginal = flattenOrder(original, guests);
            return JSON.stringify(comparableOriginal) !== JSON.stringify(edited);
        }), [editedOrders, orders, guests, flattenOrder]);

    useEffect(() => {
        const isOrderValid = (order) => {
            return order.guest_id && order.appointment_date && order.appointment_time &&
                   order.detail_name && order.detail_quantity !== '' &&
                   order.detail_category && order.detail_price !== '';
        };

        const hasPendingChanges = changedOrders.length > 0 || newOrders.length > 0 || deletedOrderIds.length > 0;
        if (!hasPendingChanges) {
            setIsSaveDisabled(true);
            return;
        }

        const allChangedValid = changedOrders.every(isOrderValid);
        const allNewValid = newOrders.every(isOrderValid);

        setIsSaveDisabled(!(allChangedValid && allNewValid));

    }, [changedOrders, newOrders, deletedOrderIds]);


    const buildGuestPatch = useCallback((value) => {
        const guest = guests.find(g => String(g.id) === String(value));
        if (guest) {
            return {
                guest_id: value,
                villa_id: guest.villa_id,
                arrival_date: guest.arrival_date,
                name: guest.first_name,
                last_name: guest.last_name,
            };
        }
        return {
            guest_id: value,
            villa_id: "",
            arrival_date: "",
            name: "",
            last_name: "",
        };
    }, [guests]);

    const handleChange = (orderId, field, value) => {
        const updated = editedOrders.map(order => {
            if (order.id === orderId) {
                let patch = { [field]: value };
                if (field === "detail_name") {
                    const menu = menus.find(m => m.name === value);
                    if (menu) {
                        patch.detail_category = menu.category || "";
                        patch.detail_price = menu.price ?? "";
                    }
                }
                if (field === "guest_id") {
                    patch = { ...patch, ...buildGuestPatch(value) };
                }
                return { ...order, ...patch };
            }
            return order;
        });
        setEditedOrders(updated);
    };

    const handleNewOrderChange = (idx, field, value) => {
        const updated = [...newOrders];
        const patch = { [field]: value };
        if (field === "detail_name") {
            const menu = menus.find(m => m.name === value);
            if (menu) {
                patch.detail_category = menu.category || "";
                patch.detail_price = menu.price ?? "";
            }
        }
        if (field === "guest_id") {
            Object.assign(patch, buildGuestPatch(value));
        }
        updated[idx] = { ...updated[idx], ...patch };
        setNewOrders(updated);
    };

    const handleAddNewOrderRow = useCallback(() => {
        const newOrder = {
            name: "", last_name: "", guest_id: "", villa_id: "", arrival_date: "",
            appointment_date: "", appointment_time: "", order_type: "", notes: "",
            confirmation_e_mail: "", detail_name: "", detail_quantity: "",
            detail_category: "", detail_price: "", detail_discount: "",
        };
        setNewOrders(prev => ([...prev, newOrder]));
        setSelectedGroupKey(toGroupKey(newOrder));
    }, [toGroupKey]);

    const getGroupRows = useCallback((groupKey) => {
        const existingRows = editedOrders
            .filter(order => toGroupKey(order) === groupKey)
            .map(order => ({ type: "existing", data: order }));
        const newRowsData = newOrders
            .map((order, idx) => ({ order, idx }))
            .filter(entry => toGroupKey(entry.order) === groupKey)
            .map(entry => ({ type: "new", data: entry.order, idx: entry.idx }));
        return [...existingRows, ...newRowsData];
    }, [editedOrders, newOrders, toGroupKey]);

    const applyHeaderFieldToGroup = useCallback((groupKey, field, value) => {
        const groupedKeyFields = new Set(["appointment_date", "appointment_time", "guest_id", "villa_id"]);
        const currentRows = getGroupRows(groupKey);
        const representative = currentRows.length > 0 ? currentRows[0].data : null;

        setEditedOrders(prev => prev.map(order => {
            if (toGroupKey(order) !== groupKey) return order;
            const patch = { [field]: value };
            if (field === "guest_id") Object.assign(patch, buildGuestPatch(value));
            return { ...order, ...patch };
        }));

        setNewOrders(prev => prev.map(order => {
            if (toGroupKey(order) !== groupKey) return order;
            const patch = { [field]: value };
            if (field === "guest_id") Object.assign(patch, buildGuestPatch(value));
            return { ...order, ...patch };
        }));

        if (representative && groupedKeyFields.has(field)) {
            const patch = { [field]: value };
            if (field === "guest_id") Object.assign(patch, buildGuestPatch(value));
            setSelectedGroupKey(toGroupKey({ ...representative, ...patch }));
        }
    }, [buildGuestPatch, getGroupRows, toGroupKey]);

    const handleAddRowToGroup = useCallback(() => {
        const rows = getGroupRows(selectedGroupKey);
        if (!rows.length) {
            handleAddNewOrderRow();
            return;
        }
        const base = rows[0].data;
        setNewOrders(prev => [
            ...prev,
            {
                name: base.name || "",
                last_name: base.last_name || "",
                guest_id: base.guest_id || "",
                villa_id: base.villa_id || "",
                arrival_date: base.arrival_date || "",
                appointment_date: base.appointment_date || "",
                appointment_time: base.appointment_time || "",
                order_type: base.order_type || "online",
                notes: base.notes || "",
                confirmation_e_mail: base.confirmation_e_mail || "",
                detail_name: "",
                detail_quantity: "",
                detail_category: "",
                detail_price: "",
                detail_discount: "",
            }
        ]);
    }, [getGroupRows, handleAddNewOrderRow, selectedGroupKey]);

    const handleResponse = async (res, defaultError) => {
        if (res.ok) {
            if (res.status === 204) return null;
            return res.json();
        }
        try {
            const err = await res.json();
            const msg = err.detail ? (Array.isArray(err.detail) ? err.detail.map(d => d.msg).join(", ") : err.detail) : JSON.stringify(err);
            return Promise.reject(new Error(msg));
        } catch {
            return Promise.reject(new Error(defaultError));
        }
    };

    const handleSave = async () => {
        if (isSaving) return;
        setErrorMsg("");

        const changedOrdersForSave = changedOrders.filter(order => !deletedOrderIds.includes(order.id));
        const ordersToDelete = orders.filter(o => deletedOrderIds.includes(o.id));

        try {
            setIsSaving(true);
            // Run requests in deterministic order to avoid DB races when add + delete happen in one save step.
            if (ordersToDelete.length > 0) {
                const deleteRes = await adminApiFetch(`${baseUrl}/orders`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(ordersToDelete)
                });
                await handleResponse(deleteRes, "Error while deleting");
            }

            if (changedOrdersForSave.length > 0) {
                const updateRes = await adminApiFetch(`${baseUrl}/orders`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(changedOrdersForSave.map(order => unflattenOrder(order, menus)))
                });
                await handleResponse(updateRes, "Error while updating");
            }

            if (newOrders.length > 0) {
                const createRes = await adminApiFetch(`${baseUrl}/orders`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(newOrders.map(order => unflattenOrder(order, menus)))
                });
                await handleResponse(createRes, "Error while creating");
            }

            await fetchAllData();
        } catch (err) {
            setErrorMsg(err.message || "Error while saving");
        } finally {
            setIsSaving(false);
        }
    };

    const handleMarkOrderForDelete = (id) => {
        setDeletedOrderIds(prev => prev.includes(id) ? prev.filter(delId => delId !== id) : [...prev, id]);
    };

    const handleRemoveNewOrderRow = (idx) => {
        setNewOrders(newOrders.filter((_, i) => i !== idx));
    };

    const groupedOrders = useMemo(() => {
        const groups = new Map();
        const changedOrderIds = new Set(changedOrders.map(order => order.id));

        for (const order of editedOrders) {
            const key = toGroupKey(order);
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    representative: order,
                    existingRows: [],
                    newRows: [],
                    totalPrice: 0,
                    markedDeleteCount: 0,
                    changedCount: 0,
                });
            }
            const group = groups.get(key);
            group.existingRows.push(order);
            if (!deletedOrderIds.includes(order.id) && changedOrderIds.has(order.id)) {
                group.changedCount += 1;
            }
            if (!deletedOrderIds.includes(order.id)) {
                const qty = Number(order.detail_quantity || 0);
                const price = Number(order.detail_price || 0);
                group.totalPrice += qty * price;
            } else {
                group.markedDeleteCount += 1;
            }
        }

        newOrders.forEach((order, idx) => {
            const key = toGroupKey(order);
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    representative: order,
                    existingRows: [],
                    newRows: [],
                    totalPrice: 0,
                    markedDeleteCount: 0,
                    changedCount: 0,
                });
            }
            const group = groups.get(key);
            group.newRows.push({ ...order, _newIdx: idx });
            const qty = Number(order.detail_quantity || 0);
            const price = Number(order.detail_price || 0);
            group.totalPrice += qty * price;
        });

        return [...groups.values()].sort((a, b) => {
            const dateCmp = (a.representative.appointment_date || "").localeCompare(b.representative.appointment_date || "");
            if (dateCmp !== 0) return dateCmp;
            return (a.representative.appointment_time || "").localeCompare(b.representative.appointment_time || "");
        });
    }, [changedOrders, deletedOrderIds, editedOrders, newOrders, toGroupKey]);

    const selectedGroup = groupedOrders.find(group => group.key === selectedGroupKey) || groupedOrders[0] || null;
    const showCreatingHint = Boolean(
        selectedGroup
        && selectedGroup.newRows.length > 0
        && (!selectedGroup.representative.appointment_date || !selectedGroup.representative.appointment_time)
    );

    const pendingCount = changedOrders.length + newOrders.length + deletedOrderIds.length;

    useEffect(() => {
        if (!groupedOrders.length) {
            if (selectedGroupKey !== "") setSelectedGroupKey("");
            return;
        }
        const hasCurrent = groupedOrders.some(group => group.key === selectedGroupKey);
        if (!hasCurrent) {
            const preferredNewGroup = [...groupedOrders].reverse().find(group => group.newRows.length > 0);
            setSelectedGroupKey((preferredNewGroup || groupedOrders[0]).key);
        }
    }, [groupedOrders, selectedGroupKey]);

    return (
        <div className="management-page">
            <div className="management-page-header">
                <h2 className="management-page-title">Order Dashboard</h2>
                <div className="management-badge-row">
                    <span className="management-badge management-badge-neutral">Groups: {groupedOrders.length}</span>
                    <span className="management-badge management-badge-new">New: {newOrders.length}</span>
                    <span className="management-badge management-badge-changed">Changed: {changedOrders.length}</span>
                    <span className="management-badge management-badge-delete">Delete: {deletedOrderIds.length}</span>
                    <span className="management-badge management-badge-neutral">Pending: {pendingCount}</span>
                </div>
            </div>

            <div className="management-callout">
                Required fields: Last Name, Appointment Date, Appointment Time, Item Name, Quantity, Category, Price. Changes are applied after clicking "Save all Changes".
            </div>

            {errorMsg && <div className="management-error">{errorMsg}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: "16px", alignItems: "start" }}>
                <aside style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "12px", maxHeight: "75vh", overflow: "auto" }}>
                    <h3 style={{ marginTop: 0 }}>Orders</h3>
                    <button type="button" onClick={handleAddNewOrderRow} style={{ marginBottom: "12px" }}>
                        Create New Order
                    </button>
                    {groupedOrders.map(group => {
                        const active = selectedGroup?.key === group.key;
                        return (
                            <button
                                key={group.key}
                                type="button"
                                onClick={() => setSelectedGroupKey(group.key)}
                                style={{
                                    width: "100%",
                                    textAlign: "left",
                                    border: active ? "2px solid #1976d2" : "1px solid #ddd",
                                    background: active ? "#eef5ff" : "#fff",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    marginBottom: "8px",
                                    cursor: "pointer",
                                }}
                            >
                                <div><strong>{group.representative.appointment_date || "no date"} {group.representative.appointment_time || ""}</strong></div>
                                <div>{group.representative.last_name || "Unknown guest"} ({group.representative.name || ""})</div>
                                <div>Items: {group.existingRows.length + group.newRows.length}</div>
                                <div>Total: {group.totalPrice.toLocaleString("en-GB")} IDR</div>
                                {group.newRows.length > 0 && (
                                    <div
                                        style={{
                                            display: "inline-block",
                                            marginTop: "6px",
                                            marginRight: "6px",
                                            backgroundColor: "#f3d36b",
                                            color: "#000",
                                            borderRadius: "999px",
                                            padding: "2px 8px",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {group.newRows.length} new
                                    </div>
                                )}
                                {group.changedCount > 0 && (
                                    <div
                                        style={{
                                            display: "inline-block",
                                            marginTop: "6px",
                                            marginRight: "6px",
                                            backgroundColor: "#1976d2",
                                            color: "#fff",
                                            borderRadius: "999px",
                                            padding: "2px 8px",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {group.changedCount} edited
                                    </div>
                                )}
                                {group.markedDeleteCount > 0 && (
                                    <div
                                        style={{
                                            display: "inline-block",
                                            marginTop: "6px",
                                            marginRight: "6px",
                                            backgroundColor: "#ef5252",
                                            color: "#fff",
                                            borderRadius: "999px",
                                            padding: "2px 8px",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {group.markedDeleteCount} marked delete
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </aside>

                <section style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "12px" }}>
                    {!selectedGroup ? (
                        <p>Please select an order group from the left list.</p>
                    ) : (
                        <>
                            <h3 style={{ marginTop: 0 }}>Order Header</h3>
                            {showCreatingHint && (
                                <div
                                    style={{
                                        display: "inline-block",
                                        marginBottom: "10px",
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        backgroundColor: "#fff4d6",
                                        color: "#6b4e00",
                                        fontWeight: 600,
                                    }}
                                >
                                    Creating new order... please select appointment date and time.
                                </div>
                            )}
                            <div className="management-order-header-grid">
                                <label className="management-order-field">
                                    Guest
                                    <select
                                        value={selectedGroup.representative.guest_id || ""}
                                        onChange={e => applyHeaderFieldToGroup(selectedGroup.key, "guest_id", e.target.value)}
                                    >
                                        <option value="">Choose Guest</option>
                                        {guests.map(guest => (
                                            <option key={guest.id} value={guest.id}>{guest.last_name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="management-order-field">
                                    Villa
                                    <select value={selectedGroup.representative.villa_id || ""} disabled>
                                        <option value="">Choose Villa</option>
                                        {villas.map(villa => (
                                            <option key={villa.id} value={villa.id}>{villa.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="management-order-field">
                                    Arrival Date
                                    <input type="date" value={selectedGroup.representative.arrival_date ? selectedGroup.representative.arrival_date.slice(0, 10) : ""} disabled />
                                </label>
                                <label className="management-order-field">
                                    Appointment Date
                                    <input
                                        type="date"
                                        value={selectedGroup.representative.appointment_date ? selectedGroup.representative.appointment_date.slice(0, 10) : ""}
                                        onChange={e => applyHeaderFieldToGroup(selectedGroup.key, "appointment_date", e.target.value)}
                                    />
                                </label>
                                <label className="management-order-field">
                                    Appointment Time
                                    <input
                                        type="time"
                                        value={selectedGroup.representative.appointment_time || ""}
                                        onChange={e => applyHeaderFieldToGroup(selectedGroup.key, "appointment_time", e.target.value)}
                                    />
                                </label>
                                <label className="management-order-field">
                                    Type
                                    <select
                                        value={selectedGroup.representative.order_type || "online"}
                                        onChange={e => applyHeaderFieldToGroup(selectedGroup.key, "order_type", e.target.value)}
                                    >
                                        <option value="online">Online</option>
                                        <option value="phone">Phone</option>
                                        <option value="e-mail">E-Mail</option>
                                        <option value="personal">Personal</option>
                                    </select>
                                </label>
                                <label className="management-order-field management-order-field-span-2">
                                    Notes
                                    <input
                                        value={selectedGroup.representative.notes || ""}
                                        onChange={e => applyHeaderFieldToGroup(selectedGroup.key, "notes", e.target.value)}
                                    />
                                </label>
                                <label className="management-order-field">
                                    Confirmation E-Mail
                                    <input
                                        type="email"
                                        value={selectedGroup.representative.confirmation_e_mail || ""}
                                        onChange={e => applyHeaderFieldToGroup(selectedGroup.key, "confirmation_e_mail", e.target.value)}
                                    />
                                </label>
                            </div>

                            <h3>Order Items</h3>
                            <table>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Item Name</th>
                                        <th>Quantity</th>
                                        <th>Category</th>
                                        <th>Price</th>
                                        <th>Discount</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedGroup.existingRows.map(order => {
                                        const isNoneCategory = order.detail_category === "none";
                                        const isDeleted = deletedOrderIds.includes(order.id);
                                        return (
                                            <tr
                                                key={order.id}
                                                style={
                                                    isDeleted
                                                        ? { backgroundColor: "#ef5252" }
                                                        : (isNoneCategory ? { backgroundColor: "#f0f0f0" } : {})
                                                }
                                            >
                                                <td>{order.id}</td>
                                                <td>
                                                    <select
                                                        value={order.detail_name || ""}
                                                        onChange={e => handleChange(order.id, "detail_name", e.target.value)}
                                                        disabled={isNoneCategory || isDeleted}
                                                    >
                                                        <option value="">Choose Item</option>
                                                        {menus.map(menu => (
                                                            <option key={menu.id} value={menu.name}>{menu.name}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td><input type="number" value={order.detail_quantity} onChange={e => handleChange(order.id, "detail_quantity", e.target.value)} disabled={isNoneCategory || isDeleted} /></td>
                                                <td>
                                                    <select value={order.detail_category || ""} onChange={e => handleChange(order.id, "detail_category", e.target.value)} disabled={isNoneCategory || isDeleted}>
                                                        <option value="">Choose Category</option>
                                                        {categories.map(cat => (
                                                            <option key={cat} value={cat}>{cat}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td><input type="number" step="0.01" value={order.detail_price} onChange={e => handleChange(order.id, "detail_price", e.target.value)} disabled={isNoneCategory || isDeleted} /></td>
                                                <td><input type="number" step="0.01" value={order.detail_discount} onChange={e => handleChange(order.id, "detail_discount", e.target.value)} disabled={isNoneCategory || isDeleted} /></td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMarkOrderForDelete(order.id)}
                                                        style={{ color: "red", background: "none", border: "none", fontSize: "1.2em", cursor: "pointer" }}
                                                        aria-label="Delete order item"
                                                        title={isDeleted ? "Undo delete order item" : "Delete order item"}
                                                    >
                                                        {isDeleted ? "↺" : "✖"}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {selectedGroup.newRows.map(row => (
                                        <tr key={`new-${row._newIdx}`} style={{ backgroundColor: "#fffbe6" }}>
                                            <td>new</td>
                                            <td>
                                                <select value={row.detail_name || ""} onChange={e => handleNewOrderChange(row._newIdx, "detail_name", e.target.value)}>
                                                    <option value="">Choose Item</option>
                                                    {menus.map(menu => (
                                                        <option key={menu.id} value={menu.name}>{menu.name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td><input type="number" value={row.detail_quantity} onChange={e => handleNewOrderChange(row._newIdx, "detail_quantity", e.target.value)} /></td>
                                            <td>
                                                <select value={row.detail_category || ""} onChange={e => handleNewOrderChange(row._newIdx, "detail_category", e.target.value)}>
                                                    <option value="">Choose Category</option>
                                                    {categories.map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td><input type="number" step="0.01" value={row.detail_price} onChange={e => handleNewOrderChange(row._newIdx, "detail_price", e.target.value)} /></td>
                                            <td><input type="number" step="0.01" value={row.detail_discount} onChange={e => handleNewOrderChange(row._newIdx, "detail_discount", e.target.value)} /></td>
                                            <td>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveNewOrderRow(row._newIdx)}
                                                    style={{ color: "red", background: "none", border: "none", fontSize: "1.2em", cursor: "pointer" }}
                                                    aria-label="Delete new order item"
                                                    title="Delete new order item"
                                                >
                                                    &#10006;
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <button type="button" onClick={handleAddRowToGroup} style={{ marginTop: "10px" }}>
                                + Add Item To Selected Order
                            </button>
                        </>
                    )}
                </section>
            </div>

            <div
                style={{
                    position: "sticky",
                    bottom: 0,
                    background: "#fff",
                    borderTop: "1px solid #ddd",
                    marginTop: "12px",
                    paddingTop: "10px",
                    opacity: isSaving ? 0.7 : 1,
                    transition: "opacity 0.2s ease",
                    cursor: isSaving ? "progress" : "default",
                }}
            >
                <button onClick={handleSave} disabled={isSaveDisabled || isSaving}>
                    {isSaving ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25" />
                                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none">
                                    <animateTransform
                                        attributeName="transform"
                                        type="rotate"
                                        from="0 12 12"
                                        to="360 12 12"
                                        dur="0.8s"
                                        repeatCount="indefinite"
                                    />
                                </path>
                            </svg>
                            Saving...
                        </span>
                    ) : "Save all Changes"}
                </button>
            </div>
        </div>
    );

}

export default Orders;